import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Gavel, Trophy } from "lucide-react";

const EXEC_IMG =
  "https://images.unsplash.com/photo-1770070553064-3980a912f96b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxleGVjdXRpdmUlMjBidXNpbmVzcyUyMG1lZXRpbmclMjBsdXh1cnklMjBkYXJrfGVufDB8fHx8MTc4OTc0MDg3OHww&ixlib=rb-4.1.0&q=85";

const BIDS = [
  { lender: "Meridian Capital Desk", rate: "1.49", term: "6 mo" },
  { lender: "Atlas Revenue Partners", rate: "1.42", term: "8 mo" },
  { lender: "Northpeak Syndicate", rate: "1.38", term: "9 mo" },
  { lender: "Helios Funding Group", rate: "1.34", term: "10 mo" },
  { lender: "Vanguard Commercial", rate: "1.29", term: "12 mo" },
];

function BiddingWar() {
  const [visible, setVisible] = useState(0);
  const [running, setRunning] = useState(false);
  const timers = useRef([]);

  const run = () => {
    timers.current.forEach(clearTimeout);
    setVisible(0);
    setRunning(true);
    BIDS.forEach((_, i) => {
      timers.current.push(setTimeout(() => setVisible(i + 1), 500 + i * 750));
    });
    timers.current.push(setTimeout(() => setRunning(false), 500 + BIDS.length * 750));
  };

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const done = visible === BIDS.length && !running;

  return (
    <div className="glass-panel rounded-md p-6 sm:p-8" data-testid="bidding-war-simulator">
      <div className="flex items-center justify-between mb-6">
        <p className="eyebrow">Live bidding war</p>
        <button
          onClick={run}
          disabled={running}
          data-testid="run-bidding-war-button"
          className="inline-flex items-center gap-2 text-xs font-mono-d tracking-[0.15em] uppercase bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-[#07090D] font-semibold px-4 py-2 rounded-sm transition-all duration-300 active:scale-95"
        >
          <Gavel className="w-3.5 h-3.5" />
          {visible === 0 ? "Run the auction" : "Run it again"}
        </button>
      </div>

      <div className="space-y-2 min-h-[240px]">
        {visible === 0 && (
          <p className="text-sm text-zinc-500 py-16 text-center font-editorial italic" data-testid="bidding-war-idle">
            Five capital desks are waiting for your file. Start the auction.
          </p>
        )}
        {BIDS.slice(0, visible).map((bid, i) => {
          const isWinner = done && i === BIDS.length - 1;
          return (
            <motion.div
              key={bid.lender}
              initial={{ opacity: 0, x: -24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className={`flex items-center justify-between border rounded-sm px-4 py-3 ${
                isWinner ? "border-amber-500/60 bg-amber-500/[0.08]" : "border-white/10 bg-white/[0.02]"
              }`}
              data-testid={`bid-row-${i}`}
            >
              <div className="flex items-center gap-3">
                {isWinner && <Trophy className="w-4 h-4 text-amber-400" />}
                <span className={`text-sm ${isWinner ? "text-zinc-100 font-medium" : "text-zinc-300"}`}>
                  {bid.lender}
                </span>
              </div>
              <div className="flex items-center gap-6 font-mono-d text-xs">
                <span className={isWinner ? "text-amber-400" : "text-zinc-400"}>factor {bid.rate}</span>
                <span className="text-zinc-500">{bid.term}</span>
              </div>
            </motion.div>
          );
        })}
      </div>

      {done && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mt-5 text-xs font-mono-d tracking-[0.15em] uppercase text-emerald-400"
          data-testid="bidding-war-result"
        >
          Factor rate compressed 13.4% · term doubled · you paid nothing for it
        </motion.p>
      )}
    </div>
  );
}

export default function BrokerAdvantageSection() {
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="broker-advantage" className="py-24 sm:py-32 bg-[#0A0D12] border-y border-white/10" data-testid="broker-advantage-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-6"
          >
            <p className="eyebrow mb-6">Why independent wins</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-normal tracking-tight leading-tight text-zinc-100">
              The Independent Broker{" "}
              <span className="font-editorial italic text-amber-400">Advantage</span>
            </h2>
            <p className="mt-6 text-base leading-relaxed text-zinc-400">
              Walk into a single funder and you get their rate, their term, their take-it-or-leave-it
              term sheet. We do the opposite. Your file is shopped across{" "}
              <span className="text-zinc-100 font-medium">30+ institutional lenders</span>{" "}
              simultaneously — and each one knows the others are bidding.
            </p>
            <p className="mt-4 text-base leading-relaxed text-zinc-400">
              That tension is the product. Desks compress factor rates, stretch terms, and strip fees
              to win the deal, because losing it to a competitor costs them more than discounting it.
              A direct lender negotiates against you.{" "}
              <span className="text-zinc-100 font-medium">We make the market negotiate against itself.</span>
            </p>
            <div ref={imgRef} className="mt-10 relative overflow-hidden rounded-sm border border-white/10 h-56 sm:h-64">
              <motion.img
                src={EXEC_IMG}
                alt="Executives reviewing capital deployment terms in a dark boardroom"
                className="w-full h-[120%] object-cover"
                style={{ y: imgY }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#07090D]/80 via-transparent to-transparent" />
              <p className="absolute bottom-4 left-4 text-xs font-mono-d tracking-[0.2em] uppercase text-zinc-400">
                Syndication desk · term sheet review
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-6"
          >
            <BiddingWar />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
